import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import DesktopLayout from "@/components/layout/DesktopLayout";
import Hey, { Trip as HeyTrip } from "@/lib/hey";
import { useFlutterBridge } from "@/hooks/useFlutterBridge";
import { BookingReferenceCard } from "@/components/new-trip/BookingReferenceCard";
import { GmailSyncCard } from "@/components/new-trip/GmailSyncCard";
import { EmailForwardCard } from "@/components/new-trip/EmailForwardCard";
import { TripDetailsDrawer } from "@/components/new-trip/TripDetailsDrawer";
import { Flight, Hotel } from "@/types/trip-types";
import { useAnalytics } from "@/hooks/use-analytics";

const NewTrip = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { 
    trackPageView, 
    trackEvent, 
    trackIndexAction,
    trackError,
    trackTripAdditionResult
  } = useAnalytics();
  
  const [bookingRef, setBookingRef] = useState("");
  const [lastName, setLastName] = useState("");
  const [tripTitle, setTripTitle] = useState("");
  const [tripType, setTripType] = useState<string>("leisure");
  const [flights, setFlights] = useState<Flight[]>([]);
  const [hotels, setHotels] = useState<Hotel[]>([]);

  useEffect(() => {
    trackPageView('/new-trip');
    trackEvent('Page', 'view', 'New Trip Page');
  }, [trackPageView, trackEvent]);

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      try {
        let data = await Hey.HeyUtils.getInstance().getUserProfile();
        if (!data) {
          throw new Error("Profile not found");
        }
        return data;
      } catch (error) {
        trackError('data_loading', String(error), 'NewTrip-ProfileQuery');
        throw error;
      }
    }
  });

  const addTripByReference = async (reference: string) => {
    try {
      console.debug('🔄 [NewTrip] Attempting to add trip by reference:', reference);
      trackEvent('NewTrip', 'add_trip_by_reference_attempt', reference);
      
      const trip = await Hey.HeyUtils.getInstance().addTripByReference(reference);
      
      if (!trip) {
        console.error('❌ [NewTrip] Failed to add trip by reference - null trip returned');
        throw new Error("Failed to add trip by reference");
      }
      
      await queryClient.invalidateQueries({ queryKey: ['trips'] });
      
      console.debug('✅ [NewTrip] Successfully added trip by reference');
      
      // Track success specifically
      trackTripAdditionResult('success', reference, `Type: ${tripType}`);
      trackIndexAction('add_new_trip', reference, `Method: Reference - Type: ${tripType}`);
      
      toast({
        title: t("newTrip.bookingSubmitted"),
        description: t("common.success"),
      });
      
      navigate("/");
    } catch (error) {
      console.error('❌ [NewTrip] Error adding trip by reference:', error);
      console.error('❌ [NewTrip] Error type:', typeof error);
      console.error('❌ [NewTrip] Full error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
      
      if (error.message === "PROFILE_MISMATCH") {
        console.warn('⚠️ [NewTrip] Profile mismatch detected, showing appropriate message');
        
        // Track profile mismatch specifically
        trackTripAdditionResult('profile_mismatch', reference, `Type: ${tripType}`);
        
        toast({
          title: t("common.error"),
          description: t("newTrip.profileMismatch"),
          variant: "destructive",
        });
      } else {
        console.warn('⚠️ [NewTrip] General error occurred, showing generic message');
        
        // Track general error
        trackError('api', String(error), 'NewTrip-AddByReference');
        trackTripAdditionResult('error', reference, String(error).substring(0, 100));
        
        toast({
          title: t("common.error"),
          description: t("newTrip.processingBooking") + " " + error,
          variant: "destructive",
        });
      }
    }
  };

  const createTripMutation = useMutation({
    mutationFn: async (data: { title: string; flights: Flight[]; hotels: Hotel[] }) => {
      trackEvent('NewTrip', 'create_manual_trip_attempt', data.title);
      
      const heyTrip: HeyTrip = {
        numberOfPassengers: 1,
        products: [],
        reference: `MAN${Date.now()}`,
        travelServiceLevel: 1
      };
      
      try {
        const trip = await Hey.HeyUtils.getInstance().addTripManually(heyTrip);
        if (!trip) {
          throw new Error("Failed to create trip");
        }
        return trip;
      } catch (error) {
        trackError('api', String(error), 'NewTrip-CreateManualTrip');
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trips'] });
      
      trackEvent('NewTrip', 'create_manual_trip_success', tripTitle || bookingRef);
      trackIndexAction('add_new_trip', `MAN${Date.now()}`, `Method: Manual - Type: ${tripType} - Flights: ${flights.length} - Hotels: ${hotels.length}`);
      
      toast({
        title: t("newTrip.bookingSubmitted"),
        description: t("common.success"),
      });
      navigate("/");
    },
    onError: (error) => {
      console.error('Error creating trip:', error);
      
      trackError('api', String(error), 'NewTrip-CreateManualTripMutation');
      trackEvent('NewTrip', 'create_manual_trip_error', String(error));
      
      toast({
        title: t("common.error"),
        description: t("newTrip.processingBooking") + " " + error,
        variant: "destructive",
      });
    }
  });

  useEffect(() => {
    if (profile?.user?.lastName) {
      setLastName(profile.user.lastName);
    }
  }, [profile]);

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      trackEvent('NewTrip', 'submit_booking_form', `Ref: ${bookingRef} - Type: ${tripType}`);
      
      await createTripMutation.mutateAsync({ 
        title: tripTitle || bookingRef || "My Trip", 
        flights: [], 
        hotels: [] 
      });
    } catch (error) {
      console.error('Error creating trip:', error);
      trackError('api', String(error), 'NewTrip-HandleBookingSubmit');
      
      toast({
        title: t("common.error"),
        description: t("newTrip.processingBooking") + " " + error,
        variant: "destructive",
      });
    }
  };

  const handleGmailConsent = () => {
    trackEvent('NewTrip', 'gmail_consent_click');
    
    toast({
      title: t("newTrip.gmailConsent"),
      description: t("newTrip.gmailProcessing"),
    });
  };

  const pageContent = (
    <div className="container px-4 py-6 space-y-6 pb-24">
      <h1 className="text-2xl font-bold mb-6">{t("newTrip.title")}</h1>

      <BookingReferenceCard
        bookingRef={bookingRef}
        lastName={lastName}
        tripType={tripType}
        onBookingRefChange={(value) => {
          setBookingRef(value);
          trackEvent('NewTrip', 'booking_ref_change');
        }}
        onLastNameChange={(value) => {
          setLastName(value);
          trackEvent('NewTrip', 'last_name_change');
        }}
        onTripTypeChange={(value) => {
          setTripType(value);
          trackEvent('NewTrip', 'trip_type_change', value);
        }}
        onSubmit={(e) => {
          e.preventDefault();
          trackEvent('NewTrip', 'booking_reference_submit', bookingRef);
          addTripByReference(bookingRef);
        }}
      />

      <GmailSyncCard onGmailConsent={handleGmailConsent} />

      <EmailForwardCard />

      <TripDetailsDrawer
        flights={flights}
        hotels={hotels}
        setFlights={(newFlights) => {
          setFlights(newFlights);
          trackEvent('NewTrip', 'flights_updated', String(newFlights.length));
        }}
        setHotels={(newHotels) => {
          setHotels(newHotels);
          trackEvent('NewTrip', 'hotels_updated', String(newHotels.length));
        }}
        tripTitle={tripTitle}
        tripType={tripType}
        onTripTitleChange={(value) => {
          setTripTitle(value);
          trackEvent('NewTrip', 'manual_trip_title_change');
        }}
        onTripTypeChange={(value) => {
          setTripType(value);
          trackEvent('NewTrip', 'manual_trip_type_change', value);
        }}
      />
    </div>
  );

  useFlutterBridge(null);

  return isMobile ? (
    <div className="min-h-screen bg-gradient-to-b from-travel-50 to-white">
      <div className="container px-4 py-4">
        {pageContent}
      </div>
    </div>
  ) : (
    <DesktopLayout>
      {pageContent}
    </DesktopLayout>
  );
};

export default NewTrip;
